"use client"
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import {
  ArrowRight,
  Bell,
  BookOpen,
  Brain,
  ChevronRight,
  CircleSlash,
  ClipboardList,
  Database,
  FolderOpen,
  Globe,
  HelpCircle,
  History,
  Home,
  Image,
  Layout,
  Link2,
  LogOut,
  MessageCircle,
  Newspaper,
  Settings,
  Share2,
  Star,
  Tags,
  Trash2,
  User,
  UserCheck,
  UserCircle,
  Users,
  X
} from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import NextImage from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRBAC } from './RBACGuard';

interface AdminSidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  isDesktopView: boolean;
  onClose: () => void;
  onToggleCollapse?: () => void;
}

// Base navigation with role permissions
const baseNavigation = [
  {
    name: 'Dashboard',
    href: '/admin',
    icon: Home,
    description: 'Overview and analytics',
    roles: ['Admin', 'Editor', 'Author'] // All roles can see dashboard
  },
  {
    name: 'Content',
    icon: FolderOpen,
    roles: ['Admin', 'Editor', 'Author'], // All roles can access content
    children: [
      {
        name: 'Blog Management',
        href: '/admin/blogs',
        icon: BookOpen,
        roles: ['Admin', 'Editor', 'Author'] // All can manage blogs (with restrictions)
      },
      {
        name: 'News-PR Management',
        href: '/admin/news-pr',
        icon: Newspaper,
        roles: ['Admin', 'Editor', 'Author'] // All can manage news-pr (with restrictions)
      },
      {
        name: 'Thought & Leadership Management',
        href: '/admin/thought-leadership',
        icon: Brain,
        roles: ['Admin', 'Editor', 'Author'] // All can manage thought-leadership (with restrictions)
      },
      {
        name: 'FAQ Management',
        href: '/admin/faqs',
        icon: HelpCircle,
        roles: ['Admin', 'Editor', 'Author'] // Admin, Editor and Author can manage FAQs (with restrictions)
      },
      {
        name: 'Categories',
        href: '/admin/categories',
        icon: Tags,
        roles: ['Admin'] // Only Admin can manage categories
      },
      {
        name: 'Testimonial Management',
        href: '/admin/testimonials',
        icon: Star,
        roles: ['Admin', 'Editor'] // Admin and Editor can manage testimonials
      },
      {
        name: 'Portfolio Management',
        href: '/admin/portfolios',
        icon: FolderOpen,
        roles: ['Admin', 'Editor'] // Admin and Editor can manage portfolios
      },
    ]
  },

  {
    name: 'Media',
    href: '/admin/media',
    icon: Image,
    description: 'Media library and assets',
    roles: ['Admin', 'Editor', 'Author'] // Admin, Editor, and Author can manage media
  },
  {
    name: 'Social Media Management',
    href: '/admin/social-media-management',
    icon: Share2,
    description: 'Facebook page posts and analytics',
    roles: ['Admin', 'Editor', 'Author']
  },

  {
    name: 'Forms & Submissions',
    icon: MessageCircle,
    description: 'Manage forms and submissions',
    roles: ['Admin', 'Editor'], // Admin and Editor can manage forms
    children: [
      {
        name: 'Contacts Form',
        href: '/admin/contacts',
        icon: MessageCircle,
        roles: ['Admin', 'Editor'] // Admin and Editor can manage contacts
      },
      {
        name: 'Blog Subscription',
        href: '/admin/subscriptions',
        icon: Bell,
        roles: ['Admin', 'Editor'] // Admin and Editor can manage subscriptions
      }
    ]
  },
  {
    name: 'Users',
    href: '/admin/users',
    icon: Users,
    description: 'Manage user accounts',
    roles: ['Admin'] // Only Admin can manage users
  },
  {
    name: 'Account Management',
    icon: UserCircle,
    description: 'Manage your personal account details',
    roles: ['Admin', 'Editor', 'Author'], // All roles can manage their own account
    children: [
      {
        name: 'Account Info',
        href: '/admin/account',
        icon: User,
        roles: ['Admin', 'Editor', 'Author']
      },
      {
        name: 'Task Management',
        href: '/admin/account/tasks',
        icon: ClipboardList,
        roles: ['Admin', 'Editor', 'Author']
      }
    ]
  },
  {
    name: 'Trash',
    href: '/admin/trash',
    icon: Trash2,
    description: 'Deleted items and restore',
    roles: ['Admin', 'Editor', 'Author'] // Admin, Editor, and Author can manage trash (with restrictions)
  },
  {
    name: 'Website-Management',
    icon: Layout,
    description: 'Manage website structure and links',
    roles: ['Admin'], // Only Admin can manage website
    children: [
      {
        name: 'Redirections',
        href: '/admin/redirects',
        icon: ArrowRight,
        roles: ['Admin']
      },
      {
        name: '410 Management',
        href: '/admin/410-management',
        icon: CircleSlash,
        roles: ['Admin']
      },
      {
        name: 'Interlinking-Management',
        href: '/admin/interlinking',
        icon: Link2,
        roles: ['Admin'] // Only Admin can manage interlinking
      },
      {
        name: 'Service Pages',
        href: '/admin/service-meta',
        icon: Globe,
        roles: ['Admin'] // Only Admin can manage service pages
      },
      {
        name: 'Pages Management',
        href: '/admin/pages-management',
        icon: Globe,
        roles: ['Admin'] // Only Admin can manage page index/visibility
      },
      {
        name: 'Post Ownership Management',
        href: '/admin/post-ownership',
        icon: UserCheck,
        roles: ['Admin'] // Only Admin can manage post ownership
      },
    ]
  },
  {
    name: 'System',
    icon: Settings,
    roles: ['Admin'], // Only Admin can access system settings
    children: [
      {
        name: 'Workflow Requests',
        href: '/admin/workflow-requests',
        icon: ClipboardList,
        roles: ['Admin']
      },
      {
        name: 'Audit Logs',
        href: '/admin/logs',
        icon: History,
        roles: ['Admin']
      },
      {
        name: 'Backup & Restore',
        href: '/admin/backup-restore',
        icon: Database,
        roles: ['Admin']
      },
      {
        name: 'Notifications',
        href: '/admin/notifications',
        icon: Bell,
        roles: ['Admin', 'Editor', 'Author'] // Admin, Editor, and Author can view notifications
      },
    ]
  },
];

export default function AdminSidebar({
  isCollapsed,
  isMobileOpen,
  isDesktopView,
  onClose,
  onToggleCollapse
}: AdminSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session } = useSession();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const flyoutCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { hasRole } = useRBAC();

  // Filter navigation based on user role
  const navigation = baseNavigation.filter(item => {
    // Check if user has access to this menu item
    if (item.roles && !hasRole(item.roles)) {
      return false;
    }

    // If item has children, filter children based on roles
    if (item.children) {
      item.children = item.children.filter(child =>
        child.roles && hasRole(child.roles)
      );

      // Only show parent if it has children or is a direct link
      return item.children.length > 0 || item.href;
    }

    return true;
  });

  useEffect(() => {
    if (isCollapsed) {
       
      setExpandedItems([]);
    }
  }, [isCollapsed]);

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (!isDesktopView) {
      onClose();
    }
  };

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => {
      // If the item is already expanded, collapse it
      if (prev.includes(itemName)) {
        return prev.filter(name => name !== itemName);
      }
      // Otherwise, expand this item and collapse all others
      return [itemName];
    });
  };

  interface NavChild { href: string }
  interface NavItem { href?: string; children?: NavChild[] }
  const isItemActive = (item: NavItem) => {
    if (item.href) {
      // Exact match for dashboard, or startsWith for other routes
      if (item.href === '/admin') {
        return pathname === item.href;
      }
      return pathname.startsWith(item.href);
    }
    if (item.children) {
      return item.children.some((child: NavChild) => {
        if (child.href === '/admin') {
          return pathname === child.href;
        }
        return pathname.startsWith(child.href);
      });
    }
    return false;
  };

  const handleLogout = async () => {
    try {
      // Clear refresh token from database
      const refreshToken = (session as unknown as { refreshToken?: string })?.refreshToken
      if (refreshToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });
      }

      // Clear local storage and cookies
      if (typeof window !== 'undefined') {
        localStorage.clear();
        sessionStorage.clear();

        // Clear NextAuth cookies
        document.cookie.split(';').forEach(c => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });
      }

      // Sign out from NextAuth without forcing a server-side redirect
      // Then navigate to the admin login page on the current origin
      await signOut({ redirect: false });
      router.push('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect even if logout fails
      router.push('/admin/login');
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && !isDesktopView && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed left-0 h-screen bg-gradient-to-b from-blue-50/80 to-blue-50/40 border-r border-blue-200/50 overflow-hidden z-[1200]
          transform transition-all duration-300 ease-in-out shadow-sm
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:relative lg:transform-none lg:shadow-none
          w-68
          ${isCollapsed ? 'lg:w-16 xl:w-16' : 'lg:w-60 xl:w-64'}
        `}
        data-collapsed={isCollapsed}
      >
        {/* Header */}
        <div className="p-3 lg:p-4 border-b border-blue-200/50 bg-white/60 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isCollapsed ? (
                <div className="w-full flex items-center justify-center">
                 <div className="w-10 h-10 flex items-center justify-center">
                  <NextImage
                    src="/site-mini-logo.svg"
                    alt="Logo"
                    width={55}
                    height={50}
                  />
                </div>
                </div>
              ) : (
                <div className="w-25 flex items-center justify-center">
                  <NextImage
                    src="/logo/logo.svg"
                    alt="Logo"
                    width={100}
                    height={100}
                  />
                </div>
              )}
            </div>
            {!isDesktopView && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-blue-100/50 transition-colors"
              >
                <X className="h-5 w-5 text-blue-600" />
              </button>
            )}
          </div>
        </div>

        <NavigationMenu.Root className="flex flex-col h-full">
          <NavigationMenu.List className={`flex-1 p-3 space-y-0.5 overflow-y-auto ${isCollapsed ? 'lg:p-1.5 lg:space-y-1' : ''}`}>
            {navigation.map((item) => {
              const isActive = isItemActive(item);
              const isExpanded = expandedItems.includes(item.name);

              if (item.children) {
                const openFlyout = () => {
                  if (flyoutCloseTimeoutRef.current) {
                    clearTimeout(flyoutCloseTimeoutRef.current);
                    flyoutCloseTimeoutRef.current = null;
                  }
                  if (isCollapsed && isDesktopView) {
                    const button = buttonRefs.current[item.name];
                    if (button) {
                      const rect = button.getBoundingClientRect();
                      const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
                      const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
                      setFlyoutPosition({
                        top: rect.top + scrollY,
                        left: rect.right + scrollX + 8
                      });
                    }
                    setHoveredItem(item.name);
                  }
                };

                const scheduleFlyoutClose = () => {
                  if (flyoutCloseTimeoutRef.current) {
                    clearTimeout(flyoutCloseTimeoutRef.current);
                  }
                  flyoutCloseTimeoutRef.current = setTimeout(() => {
                    setHoveredItem(null);
                    setFlyoutPosition(null);
                  }, 150);
                };

                const handleMouseEnter = () => {
                  openFlyout();
                };

                return (
                  <NavigationMenu.Item
                    key={item.name}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={() => {
                      if (isCollapsed && isDesktopView) {
                        scheduleFlyoutClose();
                      }
                    }}
                  >
                    <button
                      ref={(el) => { buttonRefs.current[item.name] = el; }}
                      onClick={() => !isCollapsed && toggleExpanded(item.name)}
                      title={isCollapsed ? item.name : undefined}
                      className={`w-full flex items-center ${isCollapsed ? 'lg:justify-center lg:px-0 lg:py-2' : 'justify-between'} ${isActive ? 'p-1 mx-2' : 'px-3 py-2.5'} rounded-sm text-sm font-normal transition-all duration-200 group cursor-pointer ${isActive
                        ? 'text-blue-700 bg-blue-100/50 border-l-[3px] border-blue-400 shadow-sm'
                        : 'text-gray-700 hover:bg-blue-50/70 hover:text-blue-600'
                        }`}
                    >
                      <div className={`flex items-center ${isCollapsed ? 'lg:space-x-0 lg:justify-center' : isActive ? 'space-x-3.5' : 'space-x-3'}`}>
                        <div className={`rounded-lg transition-all duration-200 backdrop-blur-md ${isCollapsed
                          ? `${isActive ? 'p-1.5' : 'p-1'} ${isActive ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm' : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'}`
                          : `${isActive ? 'p-2' : 'p-1.5'} ${isActive ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm' : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'}`
                          }`}>
                          <item.icon className={`h-5 w-5 flex-shrink-0 transition-colors ${isActive
                            ? `text-blue-500 ${isCollapsed ? 'lg:font-semibold' : ''}`
                            : 'text-blue-400 group-hover:text-blue-500'
                            } ${isCollapsed ? 'lg:mx-auto' : ''}`} />
                        </div>
                        <span className={`truncate ${isCollapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
                      </div>
                      {!isCollapsed && (
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}>
                          <ChevronRight className={`h-4 w-4 ${isActive ? 'text-blue-500' : 'text-blue-300'}`} />
                        </div>
                      )}
                    </button>

                    {/* Collapsed Hover Flyout Menu */}
                    {isCollapsed && isDesktopView && hoveredItem === item.name && flyoutPosition && typeof document !== 'undefined' &&
                      createPortal(
                        <div
                          className="fixed z-[1210] bg-white rounded-lg shadow-xl border border-blue-200/60 py-1 min-w-[200px] max-w-[250px]"
                          style={{
                            top: flyoutPosition.top,
                            left: flyoutPosition.left
                          }}
                          onMouseEnter={() => openFlyout()}
                          onMouseLeave={() => scheduleFlyoutClose()}
                        >
                          <div className="px-3 py-2 border-b border-blue-100 bg-blue-50/50">
                            <h3 className="text-sm font-medium text-blue-900">{item.name}</h3>
                          </div>
                          <div className="py-1">
                            {item.children.map((child) => {
                              const isChildActive = child.href === '/admin'
                                ? pathname === child.href
                                : pathname.startsWith(child.href);
                              return (
                                <Link
                                  key={child.name}
                                  href={child.href}
                                  onClick={handleLinkClick}
                                  className={`flex items-center ${isChildActive ? 'space-x-3.5 px-4 py-2.5' : 'space-x-3 px-4 py-2'} mx-1 rounded-lg text-sm font-normal transition-all duration-200 group ${isChildActive
                                    ? 'text-blue-700 bg-blue-100/70'
                                    : 'text-gray-700 hover:bg-blue-50/70 hover:text-blue-600'
                                    }`}
                                >
                                  <div className={`${isChildActive ? 'p-1.5' : 'p-1'} rounded-md transition-all duration-200 backdrop-blur-md ${isChildActive
                                    ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm'
                                    : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'
                                    }`}>
                                    <child.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isChildActive ? 'text-blue-500' : 'text-blue-400'}`} />
                                  </div>
                                  <span className="truncate">{child.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>,
                        document.body
                      )
                    }

                    {/* Expanded Inline Menu */}
                    {!isCollapsed && (
                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}>
                        <div className="ml-6 mt-1 space-y-0.5">
                          {item.children.map((child) => {
                            const isChildActive = child.href === '/admin'
                              ? pathname === child.href
                              : pathname.startsWith(child.href);
                            return (
                              <div key={child.name}>
                                <Link
                                  href={child.href}
                                  onClick={handleLinkClick}
                                  className={`flex items-center ${isChildActive ? 'space-x-3.5 px-2 py-1.5 mx-1' : 'space-x-3 px-3 py-2'} rounded-md text-sm font-normal transition-all duration-200 group ${isChildActive
                                    ? 'text-blue-700 bg-blue-100/70 border-l-[3px] border-blue-400'
                                    : 'text-gray-700 hover:bg-blue-50/70 hover:text-blue-600'
                                    }`}
                                >
                                  <div className={`${isChildActive ? 'p-1.5' : 'p-1'} rounded-md transition-all duration-200 backdrop-blur-md ${isChildActive
                                    ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm'
                                    : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'
                                    }`}>
                                    <child.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isChildActive ? 'text-blue-500' : 'text-blue-400'}`} />
                                  </div>
                                  <span className="truncate">{child.name}</span>
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </NavigationMenu.Item>
                );
              }

              return (
                <NavigationMenu.Item key={item.name}>
                  <NavigationMenu.Link asChild>
                    <Link
                      href={item.href!}
                      onClick={handleLinkClick}
                      title={isCollapsed ? item.name : undefined}
                      className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:px-0 lg:space-x-0 lg:py-2' : isActive ? 'space-x-3.5 p-1' : 'space-x-3 px-3 py-2.5'} rounded-sm text-sm font-normal transition-all duration-200 group ${isActive
                        ? 'text-blue-700 bg-blue-100/50 border-l-[2px] border-blue-400 shadow-sm'
                        : 'text-gray-700 hover:bg-blue-50/70 hover:text-blue-600'
                        }`}
                    >
                      <div className={`rounded-lg transition-all duration-200 backdrop-blur-md ${isCollapsed
                        ? `${isActive ? 'p-1.5' : 'p-1'} ${isActive ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm' : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'}`
                        : `${isActive ? 'p-2' : 'p-1.5'} ${isActive ? 'bg-blue-100/60 backdrop-blur-md border border-blue-200/60 shadow-sm' : 'bg-blue-50/50 backdrop-blur-md border border-blue-100/50 group-hover:bg-blue-100/60 group-hover:border-blue-200/60'}`
                        }`}>
                        <item.icon className={`h-5 w-5 flex-shrink-0 transition-colors ${isActive
                          ? `text-blue-500 ${isCollapsed ? 'lg:font-semibold' : ''}`
                          : 'text-blue-400 group-hover:text-blue-500'
                          } ${isCollapsed ? 'lg:mx-auto' : ''}`} />
                      </div>
                      <div className={`flex-1 min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
                        <span className="truncate block">{item.name}</span>
                        {item.description && (
                          <span className={`text-xs truncate block ${isActive ? 'text-blue-700' : 'text-gray-500 group-hover:text-blue-600'
                            }`}>
                            {item.description}
                          </span>
                        )}
                      </div>
                    </Link>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>
              );
            })}
          </NavigationMenu.List>

          {/* Bottom Fixed Section */}
          <div className="mt-auto border-t border-blue-200/50 bg-white/40 backdrop-blur-sm">
            {/* Expand Button (only when collapsed) */}
            {isCollapsed && isDesktopView && onToggleCollapse && (
              <div className="p-3 border-b border-blue-200/30">
                <button
                  onClick={onToggleCollapse}
                  className="w-full flex items-center justify-center p-1.5 rounded-lg bg-blue-50/50 backdrop-blur-md border border-blue-100/50 hover:bg-blue-100/60 hover:border-blue-200/60 transition-all duration-200"
                  title="Expand sidebar"
                >
                  <ChevronRight className="h-5 w-5 text-blue-400 hover:text-blue-500 transition-colors" />
                </button>
              </div>
            )}

            {/* Logout Button */}
            <div className="p-3">
              <button
                onClick={handleLogout}
                className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:px-0 lg:space-x-0' : 'space-x-3 px-3'} py-2.5 w-full text-left text-sm font-normal text-red-600 hover:bg-red-50/80 rounded-lg transition-all duration-200 group`}
                title={isCollapsed ? 'Logout' : undefined}
              >
                <LogOut className={`h-5 w-5 flex-shrink-0 transition-transform ${isCollapsed ? 'lg:mx-auto' : ''}`} />
                <span className={`${isCollapsed ? 'lg:hidden' : ''}`}>Logout</span>
              </button>
            </div>
          </div>
        </NavigationMenu.Root>
      </div>
    </>
  );
}

